import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ColDef } from 'ag-grid-community';
import { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { ShopsModel, SHOP_STATUS, CHANNELS_WITH_IMPORT, CHANNELS_WITH_FILE_IMPORT } from '../models/Shops';
import { ShopAvatar } from '../components/display/ShopAvatar';
import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { Badge } from '@shared/components/ui/badge';
import { Button } from '@shared/components/ui/button';
import { UserCard } from '@shared/components/ui/usercard';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faXmarkCircle, faGear, faClockRotateLeft } from '@fortawesome/pro-light-svg-icons';
import {
  AmazonFbmImportButton,
  AmazonFbmShipmentExportButton,
  DropdownActions,
  ImportOrderButton,
} from '../components/interaction';
import Tooltip from '@shared/components/ui/tooltip/tooltip';
import { usePermissions } from '@shared/hooks/usePermissions';

// 🎯 Función para convertir ColDef de AG Grid a ColumnDef de TanStack para ColumnSelector
const convertToTanStackColumns = (agGridColumns: ColDef[]): ColumnDef<ShopsModel>[] => {
  return agGridColumns
    .filter((col) => {
      // 🎯 Excluir columnas que no deben ser opcionales
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actionsShops' && field !== 'unknown';
    })
    .map((col) => ({
      id: col.field || col.headerName || 'unknown',
      accessorKey: col.field || col.headerName || 'unknown',
      header: col.headerName || 'Columna',
      cell: col.cellRenderer ? () => null : undefined, // ColumnSelector no necesita cell renderers
    }));
};

// 🎯 Tipos para los handlers
interface UseAgGridColumnsProps {
  hasTeamData: boolean;
  selectedColumns?: string[];
  onOpenSettings?: (shop: ShopsModel) => void;
  onToggleStatus?: (shop: ShopsModel) => void;
  onDelete?: (shop: ShopsModel) => void;
  refetch?: () => void;
}

// 🎯 Hook para manejar las columnas de AG Grid y ColumnSelector
export const useAgGridColumns = ({
  hasTeamData,
  selectedColumns = [],
  onOpenSettings,
  onToggleStatus,
  onDelete,
  refetch,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('shopsModule');
  const { hasPermission } = usePermissions();

  // 🎯 Verificar permisos de tiendas
  // NOTA: shops.manage NO está documentado y no debe usarse (problema de backend)
  // NOTA: shops.configure NO se usa para editar tiendas, solo para configuración de shipping
  const canEditShops = hasPermission('shops.edit');
  const canDeleteShops = hasPermission('shops.delete');
  const canActivateShops = hasPermission('shops.activate');

  // 🎯 Definiciones completas de columnas para AG Grid
  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // 🔵 COLUMNA: Nombre de la tienda
      {
        field: 'name',
        headerName: t('table.name'),
        minWidth: 200,
        sortable: false, // 🎯 Shops no tiene ordenamiento
        // 🎯 CELDA: Nombre con imagen/avatar
        cellRenderer: (params: any) => {
          const shop = params.data as ShopsModel;
          return (
            <div className="flex items-center gap-3">
              <ShopAvatar shop={shop} size="md" />
              <span className="text-sm font-semibold text-title">{shop.name}</span>
            </div>
          );
        },
      },

      // 🔵 COLUMNA: Estado de la tienda
      {
        headerName: t('table.status'),
        field: 'status',
        minWidth: 120, // 🎯 Shops: Ocupar máximo ancho, con un mínimo
        sortable: false, // 🎯 Shops no tiene ordenamiento
        valueGetter: (params) => (params.data.status > 0 ? 'Activa' : 'Inactiva'),
        // 🎯 CELDA: Badge con icono de estado
        cellRenderer: (params: any) => {
          const shop = params.data as ShopsModel;
          return (
            <Badge
              variant="outline"
              className={`flex items-center gap-2 text-xs w-fit border-0 py-1 ${
                shop.status > 0 ? 'bg-card-border light:bg-bg-success' : 'bg-card-border light:bg-bg-error'
              }`}
            >
              <FontAwesomeIcon
                icon={shop.status > 0 ? faCheckCircle : faXmarkCircle}
                className={`w-3 h-3 ${shop.status > 0 ? 'text-success dark:text-bright-success' : 'text-error'}`}
              />
              <span className={`${shop.status > 0 ? 'text-success dark:text-text' : 'text-error dark:text-text'}`}>
                {shop.status > 0 ? SHOP_STATUS[1] : SHOP_STATUS[0]}
              </span>
            </Badge>
          );
        },
      },

      // 🔵 COLUMNA: Sincronización
      {
        headerName: t('table.sync'),
        field: 'last_synchronization',
        minWidth: 140, // 🎯 Shops: Ocupar máximo ancho, con un mínimo
        sortable: false, // 🎯 Shops no tiene ordenamiento
        valueGetter: (params) => params.data.last_synchronization,
        // 🎯 CELDA: Badge con tooltip de última sincronización
        cellRenderer: (params: any) => {
          const shop = params.data as ShopsModel;
          const raw = shop.last_synchronization as string | null;

          if (!raw) return <span className="text-sm">-</span>;

          const formatted = new Date(raw).toLocaleString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          });

          return (
            <div className="flex w-full">
              <Tooltip
                className="bg-card-bg"
                content={
                  <div className="text-xs font-medium text-title text-center p-1">
                    {t('sync.lastSync')}: {formatted}
                  </div>
                }
                trigger={
                  <Badge
                    variant="outline"
                    className="flex items-center gap-2 text-xs w-fit bg-card-border cursor-pointer border-0 py-1 light:bg-bg-success"
                  >
                    <div className="w-3 h-3 bg-success dark:bg-bright-success rounded-full"></div>
                    <span className="text-xs font-medium text-title light:text-success">{t('sync.ready')}</span>
                  </Badge>
                }
              />
            </div>
          );
        },
      },
    ];

    // 🔵 COLUMNA: Cliente del equipo (team) - Solo si hay datos
    if (hasTeamData) {
      baseColumns.push({
        headerName: t('table.client'),
        field: 'team',
        minWidth: 150, // 🎯 Shops: Ocupar máximo ancho, con un mínimo
        valueGetter: (params) => params.data.team?.name,
        // 🎯 CELDA: UserCard con nombre del team
        cellRenderer: (params: any) => {
          const shop = params.data as ShopsModel;
          return shop.team?.name ? (
            <UserCard name={shop.team.name} email="" />
          ) : (
            <span className="text-sm text-complementary-text">-</span>
          );
        },
      });
    }

    // 🔵 COLUMNA: Acciones (fijada a la derecha)
    baseColumns.push({
      headerName: '',
      field: 'actionsShops',
      width: 230, // 🎯 Ancho fijo (import + export FBM + resto de acciones)
      minWidth: 200, // 🎯 Ancho mínimo
      resizable: true, // 🎯 Permitir redimensionamiento
      suppressMovable: true, // 🎯 NO permitir arrastrar la columna
      pinned: 'right', // 🎯 Fijar columna a la derecha
      lockPosition: 'right', // 🎯 Bloquear posición para que no se pueda mover
      cellStyle: {
        minWidth: '200px', // 🎯 Asegurar espacio mínimo
        padding: '8px', // 🎯 Espaciado interno
      },
      // 🎯 CELDA: Botones de acciones
      cellRenderer: (params: any) => {
        const shop = params.data as ShopsModel;

        return (
          <div className="flex w-full items-center justify-end gap-2">
            {CHANNELS_WITH_IMPORT.includes(shop.channel_id) && (
              <ImportOrderButton shopId={shop.id} channelId={shop.channel_id} />
            )}
            {CHANNELS_WITH_FILE_IMPORT.includes(shop.channel_id) && (
              <>
                <AmazonFbmImportButton shopId={shop.id} onImportSuccess={refetch} />
                <AmazonFbmShipmentExportButton shopId={shop.id} onExportSuccess={refetch} />
              </>
            )}
            {CHANNELS_WITH_IMPORT.includes(shop.channel_id) && (
              <Button
                asChild
                variant="outline"
                size="sm"
                title={t('buttons.history', 'Historial')}
                data-testid="shop-history-button"
              >
                <Link to={`/shops/import-history/${shop.id}`}>
                  <FontAwesomeIcon icon={faClockRotateLeft} className="!w-3.5 !h-3.5 text-icons" />
                </Link>
              </Button>
            )}
            {/* Botón de configuración - solo si tiene permiso de editar */}
            {canEditShops && onOpenSettings && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenSettings(shop)}
                title="Configuración"
                data-testid="shop-settings-button"
              >
                <FontAwesomeIcon icon={faGear} className="!w-3.5 !h-3.5 text-icons" />
              </Button>
            )}
            {/* Dropdown de acciones - solo si tiene algún permiso */}
            {(canActivateShops || canDeleteShops || canEditShops) && (
              <DropdownActions
                shopId={shop.id}
                shopStatus={shop.status}
                channelId={shop.channel_id}
                onToggleStatus={canActivateShops ? () => onToggleStatus?.(shop) : undefined}
                onDelete={canDeleteShops ? () => onDelete?.(shop) : undefined}
                onOpenSettings={canEditShops && onOpenSettings ? () => onOpenSettings(shop) : undefined}
              />
            )}
          </div>
        );
      },
    });

    return baseColumns;
  }, [
    t,
    hasTeamData,
    onOpenSettings,
    onToggleStatus,
    onDelete,
    refetch,
    canEditShops,
    canDeleteShops,
    canActivateShops,
  ]);

  // 🎯 Filtrar columnas basado en la selección del ColumnSelector
  const filteredColumnDefs: ColDef[] = useMemo(() => {
    // 🎯 Columnas que siempre deben estar visibles
    const alwaysVisibleColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      // Mantener siempre 'actionsShops' y 'name' para evitar estados sin columnas de datos
      return field === 'actionsShops' || field === 'name' || field === 'unknown';
    });

    // 🎯 Columnas seleccionables
    const selectableColumns = allColumnDefs.filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actionsShops' && field !== 'unknown';
    });

    // Utilidad para evitar columnas duplicadas por `field`
    const dedupeByField = (cols: ColDef[]) => {
      const seen = new Set<string>();
      const result: ColDef[] = [];
      cols.forEach((c) => {
        const id = (c.field || c.headerName || 'unknown') as string;
        if (!seen.has(id)) {
          seen.add(id);
          result.push(c);
        }
      });
      return result;
    };

    if (selectedColumns.length === 0) {
      // Si no hay selección, mostrar todas las columnas seleccionables + las siempre visibles (sin duplicados)
      return dedupeByField([...selectableColumns, ...alwaysVisibleColumns]);
    }

    // Filtrar columnas seleccionables basado en la selección
    const selectedSelectableColumns = selectableColumns.filter((col) => {
      const fieldId = col.field || col.headerName || 'unknown';
      return selectedColumns.includes(fieldId);
    });

    // Combinar columnas seleccionadas + siempre visibles SIN duplicados
    const combined = [...selectedSelectableColumns];
    alwaysVisibleColumns.forEach((c) => {
      const field = c.field || c.headerName || 'unknown';
      const exists = combined.some((x) => (x.field || x.headerName || 'unknown') === field);
      if (!exists) combined.push(c);
    });

    return combined;
  }, [allColumnDefs, selectedColumns]);

  // 🎯 Columnas para el ColumnSelector (formato TanStack)
  const tanStackColumns: ColumnDef<ShopsModel>[] = useMemo(() => {
    return convertToTanStackColumns(allColumnDefs);
  }, [allColumnDefs]);

  return {
    allColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
